import { useCallback, useState } from 'react'
import { newId } from '@/lib/ids'
import { parsePctTenths, readingInRange, tenthsToString } from '../../model/moisture'
import {
  Btn,
  Cta,
  Footer,
  Grow,
  Icon,
  Keypad,
  Readout,
  Row,
  Strip,
  V1Screen,
  WaitCta,
} from '../kit'
import { C, typeKey } from '../style'
import { cropName, tr } from '../text'
import { Rich } from '../Rich'
import { StepHeader } from './shared'
import { useV1 } from './ctx'
import { OptionalPhoto } from '../OptionalPhoto'

/** F4.4 — नमी (groundnut): one reading from any good bag, or आज मीटर नहीं. Nothing is held. */
export function MoistureScreen({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const { d, dispatch, roster, stamp } = useV1()
  const cfg = roster.config
  // Live days: a saved reading can be changed until he agrees the slip ("Change" opens the keypad
  // again). Paper back-entry keeps it as typed.
  const canChange = d.capture_mode === 'APP' && d.slip.farmer_agreed_at === null
  const [editing, setEditing] = useState(false)
  const saved = d.moisture.saved && !editing
  const photoRequired = d.reading_photos_required === true
  const [v, setV] = useState(() =>
    saved && d.moisture.readings[0] ? tenthsToString(d.moisture.readings[0].tenths) : '',
  )
  /** Optional: a live photo of the meter's display with the reading. */
  const [meterPhoto, setMeterPhoto] = useState<string | null>(
    () => d.moisture.readings[0]?.photo_media_id ?? null,
  )
  const tenths = v === '' ? null : parsePctTenths(v)
  const ok =
    tenths !== null && readingInRange(tenths, cfg.moisture_min_tenths, cfg.moisture_max_tenths)
  const onKey = useCallback(
    (k: string) => {
      if (!saved) setV((c) => typeKey(c, k, { decimals: 1, max: 3 }))
    },
    [saved],
  )
  const crop = cropName(cfg.crops, d.crop_id)

  return (
    <V1Screen
      main={{ padding: '8px 14px', gap: 8 }}
      header={
        <StepHeader
          step="moisture"
          icon="drop"
          title={tr('gate.v1.moisture.title')}
          onBack={onBack}
        />
      }
      footer={
        <Footer dense>
          {saved ? (
            <>
              {canChange && (
                <Row>
                  <Btn
                    icon="pencil"
                    onClick={() => {
                      // A fresh meter photo for the new reading; the saved one stays with the
                      // saved reading until Save replaces it (a retake would delete it).
                      setMeterPhoto(null)
                      setEditing(true)
                    }}
                  >
                    {tr('gate.v1.moisture.change')}
                  </Btn>
                </Row>
              )}
              <Cta label={tr('gate.v1.next')} icon="next" height={56} onClick={onNext} />
            </>
          ) : (
            <>
              <Row>
                <Btn
                  icon="nodrop"
                  onClick={() => {
                    dispatch({ type: 'no_meter' })
                    onNext()
                  }}
                >
                  {tr('gate.v1.moisture.noMeter')}
                </Btn>
              </Row>
              {!ok || (photoRequired && !meterPhoto) ? (
                <WaitCta height={56}>
                  {!ok ? tr('gate.v1.moisture.type') : tr('gate.v1.moisture.photo')}
                </WaitCta>
              ) : (
                <Cta
                  label={tr('gate.v1.save')}
                  icon="ok"
                  height={56}
                  onClick={() => {
                    if (!ok || tenths === null || (photoRequired && !meterPhoto)) return
                    dispatch({
                      type: 'save_moisture',
                      readings: [
                        {
                          id: newId(),
                          tenths,
                          bag_no: null,
                          taken_at: stamp(),
                          chosen_by_farmer: false,
                          photo_media_id: meterPhoto,
                        },
                      ],
                    })
                    onNext()
                  }}
                />
              )}
            </>
          )}
        </Footer>
      }
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '6px 10px',
          borderRadius: 10,
          background: C.sunk,
          fontSize: 16,
          lineHeight: 1.3,
        }}
      >
        <Icon name="groundnut" size={22} style={{ color: C.acc }} />
        <span>
          <Rich text={tr('gate.v1.moisture.meterSet', { crop })} />
        </span>
      </div>
      {saved && d.moisture.no_meter ? (
        <Strip icon="nodrop">{tr('gate.v1.moisture.noMeterSaved')}</Strip>
      ) : (
        <Readout
          label={tr('gate.v1.moisture.anyBag')}
          value={v}
          unit="%"
          size={44}
          padding="6px 12px 8px"
          labelSize={16}
          speakText={v ? tr('gate.v1.spoken.percent', { value: v }) : tr('gate.v1.moisture.anyBag')}
        />
      )}
      {v !== '' && !ok && !saved && (
        <div style={{ fontSize: 16, color: C.bad, fontWeight: 700 }}>
          {tr('gate.v1.moisture.range', {
            min: tenthsToString(cfg.moisture_min_tenths),
            max: tenthsToString(cfg.moisture_max_tenths),
          })}
        </div>
      )}
      {!saved && (
        <OptionalPhoto
          value={meterPhoto}
          onChange={setMeterPhoto}
          label={tr('gate.v1.moisture.photo')}
          takenLabel={tr('gate.v1.moisture.photoTaken')}
          retakeLabel={tr('gate.v1.moisture.photoRetake')}
          required={photoRequired}
        />
      )}
      <Grow />
      {!saved && <Keypad onKey={onKey} />}
    </V1Screen>
  )
}
